import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRight, FileText, Sparkles, Loader2, ScrollText, CheckCircle2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function TestSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [pagesPerTest, setPagesPerTest] = useState(1);
  const [sessionId, setSessionId] = useState(editId);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsingStatement, setParsingStatement] = useState(false);
  const [statementUploaded, setStatementUploaded] = useState(false);

  const [qText, setQText] = useState("");
  const [qType, setQType] = useState("open_answer");
  const [qAnswer, setQAnswer] = useState("");
  const [qScore, setQScore] = useState("");

  const [editQ, setEditQ] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (editId) loadSession();
  }, [editId]);

  const loadSession = async () => {
    try {
      const [sRes, qRes] = await Promise.all([
        api.get(`/sessions/${editId}`),
        api.get(`/sessions/${editId}/questions`),
      ]);
      setTitle(sRes.data.title);
      setSubject(sRes.data.subject || "");
      setPagesPerTest(sRes.data.pages_per_test || 1);
      setStatementUploaded(!!sRes.data.statement_file_path);
      setQuestions(qRes.data);
    } catch {
      toast.error("Erro ao carregar sessão");
    }
  };

  const ensureSession = async () => {
    if (sessionId) {
      await api.put(`/sessions/${sessionId}`, { title, subject, pages_per_test: pagesPerTest });
      return sessionId;
    }
    if (!title.trim()) {
      toast.error("Preencha o título do teste");
      return null;
    }
    setSaving(true);
    try {
      const res = await api.post("/sessions", { title, subject, pages_per_test: pagesPerTest });
      setSessionId(res.data.id);
      return res.data.id;
    } catch {
      toast.error("Erro ao guardar");
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Merge extracted questions into the session, avoiding duplicates by question number.
  // source "statement" fills full question text; source "corrigenda" fills answers + scores.
  const upsertQuestions = async (sid, extracted, source) => {
    const { data: current } = await api.get(`/sessions/${sid}/questions`);
    let changed = 0;
    for (const eq of extracted) {
      const num = eq.number;
      const existing = num != null ? current.find((q) => q.number === num) : null;
      if (existing) {
        const update = {};
        if (source === "statement") {
          if (eq.text && eq.text.length > (existing.text || "").length) update.text = eq.text;
          if (eq.type) update.type = eq.type;
        } else {
          if (eq.answer_key) update.answer_key = eq.answer_key;
          if (eq.suggested_score != null) update.max_score = eq.suggested_score;
          if (eq.type && (!existing.type || existing.type === "open_answer")) update.type = eq.type;
        }
        if (Object.keys(update).length > 0) {
          try { await api.put(`/sessions/${sid}/questions/${existing.id}`, update); changed++; } catch {}
        }
      } else {
        try {
          await api.post(`/sessions/${sid}/questions`, {
            number: num || current.length + changed + 1,
            text: eq.text || `Questão ${num || ""}`,
            type: eq.type || "open_answer",
            answer_key: eq.answer_key || "",
            max_score: eq.suggested_score || 5,
          });
          changed++;
        } catch {}
      }
    }
    return changed;
  };

  const handleStatementUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const sid = await ensureSession();
    if (!sid) { e.target.value = ""; return; }

    setParsingStatement(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/sessions/${sid}/parse-statement`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });

      const extracted = res.data.questions || [];
      const n = await upsertQuestions(sid, extracted, "statement");

      if (res.data.detected_subject && !subject.trim()) {
        setSubject(res.data.detected_subject);
        toast.success(`Disciplina detetada: ${res.data.detected_subject}`);
      }

      if (n > 0) toast.success(`${n} questões extraídas do enunciado!`);
      else toast.success("Enunciado carregado com sucesso!");

      const qRes = await api.get(`/sessions/${sid}/questions`);
      setQuestions(qRes.data);
      setStatementUploaded(true);
    } catch (err) {
      toast.error("Erro ao processar enunciado");
    } finally {
      setParsingStatement(false);
      e.target.value = "";
    }
  };

  const handleCorrigendaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const sid = await ensureSession();
    if (!sid) { e.target.value = ""; return; }

    setParsing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/sessions/${sid}/parse-corrigenda`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });

      const extracted = res.data.questions || [];

      if (res.data.detected_subject && !subject.trim()) {
        setSubject(res.data.detected_subject);
        toast.success(`Disciplina detetada: ${res.data.detected_subject}`);
      }

      if (extracted.length === 0) {
        toast.error("Não foi possível extrair questões. Adicione manualmente.");
        return;
      }

      const n = await upsertQuestions(sid, extracted, "corrigenda");
      toast.success(`Corrigenda processada — ${n} questões atualizadas/adicionadas!`);

      const qRes = await api.get(`/sessions/${sid}/questions`);
      setQuestions(qRes.data);
    } catch (err) {
      toast.error("Erro ao processar corrigenda");
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  };

  const addQuestion = async () => {
    if (!qText.trim() || !qAnswer.trim() || !qScore) {
      toast.error("Preencha todos os campos da questão");
      return;
    }
    const sid = await ensureSession();
    if (!sid) return;

    try {
      const res = await api.post(`/sessions/${sid}/questions`, {
        number: questions.length + 1,
        text: qText,
        type: qType,
        answer_key: qAnswer,
        max_score: parseFloat(qScore),
      });
      setQuestions([...questions, res.data]);
      setQText("");
      setQAnswer("");
      setQScore("");
      toast.success("Questão adicionada");
    } catch {
      toast.error("Erro ao adicionar questão");
    }
  };

  const deleteQuestion = async (qId) => {
    try {
      await api.delete(`/sessions/${sessionId}/questions/${qId}`);
      setQuestions(questions.filter((q) => q.id !== qId));
      toast.success("Questão eliminada");
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  const openEdit = (q) => {
    setEditQ({
      id: q.id,
      number: q.number,
      text: q.text || "",
      type: q.type || "open_answer",
      answer_key: q.answer_key || "",
      max_score: q.max_score,
    });
  };

  const saveEdit = async () => {
    if (!editQ.text.trim()) {
      toast.error("O texto da questão é obrigatório");
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/sessions/${sessionId}/questions/${editQ.id}`, {
        text: editQ.text,
        type: editQ.type,
        answer_key: editQ.answer_key,
        max_score: parseFloat(editQ.max_score) || 0,
      });
      const qRes = await api.get(`/sessions/${sessionId}/questions`);
      setQuestions(qRes.data);
      setEditQ(null);
      toast.success("Questão atualizada");
    } catch {
      toast.error("Erro ao atualizar questão");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleProceed = async () => {
    const sid = await ensureSession();
    if (!sid) return;
    if (questions.length === 0 && !statementUploaded) {
      toast.error("Adicione pelo menos uma questão ou carregue o enunciado do teste");
      return;
    }
    navigate(`/tests/${sid}/upload`);
  };

  const totalScore = questions.reduce((a, q) => a + q.max_score, 0);

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="test-setup-page">
      <div>
        <h1 className="font-heading font-bold text-3xl tracking-tight">
          {editId ? "Editar Teste" : "Novo Teste"}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">Defina as questões, respostas corretas e cotações</p>
      </div>

      {/* Basic Info */}
      <Card>
        <CardHeader><CardTitle className="text-lg font-heading">Informações do Teste</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Título do Teste</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Ficha de Avaliação - Matemática 3.º ano" data-testid="test-title" />
            </div>
            <div className="space-y-2">
              <Label>Disciplina <span className="text-muted-foreground font-normal">(opcional)</span></Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A IA deteta automaticamente" data-testid="test-subject" />
              <p className="text-xs text-muted-foreground">Deixe em branco — a IA identifica a disciplina ao analisar o enunciado ou a corrigenda.</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Máximo de páginas por teste</Label>
            <p className="text-xs text-muted-foreground">Até quantas fotos/páginas pode ter o teste de cada aluno? (pode ser menos)</p>
            <Input
              type="number"
              min="1"
              max="10"
              value={pagesPerTest}
              onChange={(e) => setPagesPerTest(parseInt(e.target.value) || 1)}
              className="w-32 font-mono"
              data-testid="pages-per-test"
            />
          </div>
        </CardContent>
      </Card>

      {/* Enunciado (Statement) Upload */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-primary" />
            Enunciado do Teste (Extração Automática)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Carregue o PDF do enunciado (o teste que os alunos recebem). A IA extrai o texto completo de cada questão e deteta a disciplina automaticamente.
          </p>
          <input type="file" accept=".pdf,image/*" onChange={handleStatementUpload} className="hidden" id="statement-upload" data-testid="statement-input" />
          <div className="flex items-center gap-3 flex-wrap">
            <label htmlFor="statement-upload">
              <Button asChild variant="outline" disabled={parsingStatement}>
                <span>
                  {parsingStatement ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScrollText className="h-4 w-4 mr-2" />}
                  {parsingStatement ? "A analisar enunciado..." : "Carregar Teste (Enunciado)"}
                </span>
              </Button>
            </label>
            {statementUploaded && !parsingStatement && (
              <span className="inline-flex items-center gap-1.5 text-sm text-green-600 font-medium" data-testid="statement-uploaded-badge">
                <CheckCircle2 className="h-4 w-4" /> Enunciado carregado
              </span>
            )}
          </div>
          {parsingStatement && (
            <p className="text-xs text-primary animate-pulse">A IA está a analisar o enunciado. Isto pode demorar até 30 segundos...</p>
          )}
        </CardContent>
      </Card>

      {/* Corrigenda Upload */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Corrigenda (Extração Automática)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Carregue o PDF da corrigenda e a IA preencherá as respostas corretas e sugestões de cotação para cada questão.
          </p>
          <input type="file" accept=".pdf,image/*" onChange={handleCorrigendaUpload} className="hidden" id="corrigenda-upload" data-testid="corrigenda-input" />
          <label htmlFor="corrigenda-upload">
            <Button asChild variant="outline" disabled={parsing}>
              <span>
                {parsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                {parsing ? "A analisar corrigenda..." : "Carregar Corrigenda (PDF)"}
              </span>
            </Button>
          </label>
          {parsing && (
            <p className="text-xs text-primary animate-pulse">A IA está a analisar o documento. Isto pode demorar até 30 segundos...</p>
          )}
        </CardContent>
      </Card>

      {/* Questions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center justify-between">
            <span>Questões ({questions.length})</span>
            {totalScore > 0 && <Badge variant="outline" className="font-mono">Total: {totalScore} pts</Badge>}
          </CardTitle>
          {questions.length === 0 && statementUploaded && (
            <p className="text-xs text-muted-foreground" data-testid="questions-optional-hint">
              Enunciado carregado — não é obrigatório adicionar questões manualmente. Pode avançar diretamente.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {questions.map((q) => (
            <div key={q.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 group" data-testid={`question-${q.id}`}>
              <span className="font-mono text-sm font-bold text-primary mt-0.5 w-6 shrink-0">{q.number}.</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug">{q.text}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className="text-xs text-muted-foreground">Resp: <span className="font-medium text-foreground">{q.answer_key}</span></span>
                  <span className="text-xs text-muted-foreground">&middot; {q.max_score} pts</span>
                  <Badge variant="outline" className="text-xs">
                    {q.type === "multiple_choice" ? "Escolha Múltipla" : "Resposta Aberta"}
                  </Badge>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => openEdit(q)} className="opacity-0 group-hover:opacity-100 text-primary shrink-0" data-testid={`edit-q-${q.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => deleteQuestion(q.id)} className="opacity-0 group-hover:opacity-100 text-destructive shrink-0" data-testid={`delete-q-${q.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Separator />

          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Adicionar questão manualmente</p>
            <Textarea value={qText} onChange={(e) => setQText(e.target.value)} placeholder="Texto da questão..." rows={2} data-testid="question-text" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={qType} onValueChange={setQType}>
                <SelectTrigger data-testid="question-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open_answer">Resposta Aberta</SelectItem>
                  <SelectItem value="multiple_choice">Escolha Múltipla</SelectItem>
                </SelectContent>
              </Select>
              <Input value={qAnswer} onChange={(e) => setQAnswer(e.target.value)} placeholder="Resposta correta" data-testid="question-answer" />
              <Input type="number" step="0.5" min="0" value={qScore} onChange={(e) => setQScore(e.target.value)} placeholder="Cotação (pts)" data-testid="question-score" />
            </div>
            <Button onClick={addQuestion} variant="outline" className="w-full" data-testid="add-question-btn">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Questão
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleProceed} disabled={saving} size="lg" className="active:scale-95 transition-transform" data-testid="proceed-upload-btn">
          {saving ? "A guardar..." : "Guardar e Carregar Testes"}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Edit Question Dialog */}
      <Dialog open={!!editQ} onOpenChange={(open) => !open && setEditQ(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-question-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Questão {editQ?.number}</DialogTitle>
          </DialogHeader>
          {editQ && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Texto da questão</Label>
                <Textarea
                  value={editQ.text}
                  onChange={(e) => setEditQ({ ...editQ, text: e.target.value })}
                  rows={3}
                  data-testid="edit-question-text"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2 sm:col-span-1">
                  <Label>Tipo</Label>
                  <Select value={editQ.type} onValueChange={(v) => setEditQ({ ...editQ, type: v })}>
                    <SelectTrigger data-testid="edit-question-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open_answer">Resposta Aberta</SelectItem>
                      <SelectItem value="multiple_choice">Escolha Múltipla</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <Label>Resposta correta</Label>
                  <Input
                    value={editQ.answer_key}
                    onChange={(e) => setEditQ({ ...editQ, answer_key: e.target.value })}
                    data-testid="edit-question-answer"
                  />
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <Label>Cotação (pts)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editQ.max_score}
                    onChange={(e) => setEditQ({ ...editQ, max_score: e.target.value })}
                    data-testid="edit-question-score"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditQ(null)} data-testid="edit-question-cancel">Cancelar</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-question-save">
              {savingEdit ? "A guardar..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
